import { addMonths } from 'date-fns';
import { useDateNow, useReduxState } from 'hooks';
import { getFormattedDate } from 'lib/common';
import { getAllLanguageOptionsByLangCode } from 'lib/courses';
import { Badge, FlagIcon } from 'components/common';
import { Icon } from 'components/common/icons';
import { CourseProgressTooltip } from '.';

import type { Course, CourseCompletion, CourseProgress } from 'shared-types';

interface Props {
  completion: CourseCompletion;
  course?: Pick<Course, 'language' | 'languageVersions'>;
  discrepancyInCompletion?: boolean;
  progressData?: Pick<
    CourseProgress,
    | 'discrepancyInCompletion'
    | 'numberOfTasks'
    | 'numberOfVideos'
    | 'readingProgress'
    | 'taskAnswers'
    | 'taskMaxPoints'
    | 'taskPoints'
    | 'taskPointsProgress'
    | 'videoProgress'
    | 'videoProgressVideosWatched'
  >;
  showProgressData?: boolean;
  showText?: boolean;
  showTextShort?: boolean;
  showTooltip?: boolean;
  style?: React.CSSProperties;
}

const CompletionStatus = ({
  completion,
  course,
  discrepancyInCompletion = false,
  progressData,
  showProgressData,
  showText,
  showTextShort,
  showTooltip = true,
  style = {}
}: Props) => {
  const { dateNow } = useDateNow();

  const { messages, serverConfig } = useReduxState();

  if (
    !completion ||
    completion.inactive ||
    completion.status === 'inprogress'
  ) {
    return null;
  }

  const {
    completionDate,
    expirationDate,
    readingProgress,
    status,
    tasksPoints,
    videoProgress
  } = completion;

  const expired =
    expirationDate && Date.parse(expirationDate as string) < dateNow;

  const expiring =
    !expired &&
    expirationDate &&
    new Date(Date.parse(expirationDate as string)) < addMonths(dateNow, 3);

  const text =
    status === 'failed'
      ? messages.readerCompletionFailed
      : status === 'waiting-for-reviews'
      ? messages.completionsWaitingForReviews
      : showText
      ? expired
        ? messages.completionsExpired
        : expiring
        ? messages.completionsExpiring
        : messages.commonCompleted
      : showTextShort
      ? expired
        ? messages.commonExpired
        : expiring
        ? messages.commonExpiring
        : messages.commonCompleted
      : getFormattedDate({ date: completionDate });

  const languageOptions = getAllLanguageOptionsByLangCode();
  const languageOption = languageOptions[completion.courseLanguageVersion];

  const hasManuallySetIcon = status === 'success' && completion.isManuallySet;
  const hasElectronicSignatureIcon =
    status === 'success' && Boolean(completion.electronicSignatureText);
  const hasCourseLanguageVersionIcon =
    completion?.courseLanguageVersion &&
    Object.keys(course?.languageVersions || {}).length > 0;

  const badgeStatusColor =
    status === 'failed'
      ? 'error-light'
      : status === 'waiting-for-reviews'
      ? 'default'
      : expired
      ? 'error-light'
      : expiring
      ? 'warning-light'
      : 'success-light';

  return (
    <>
      <CourseProgressTooltip
        attribute={`data-completion-progress-status-${completion.id}`}
        completionDate={completionDate as string}
        completionStatus={status}
        completionUpdatedAt={completion.updatedAt as string}
        discrepancyInCompletion={progressData?.discrepancyInCompletion}
        expirationDate={expirationDate as string}
        numberOfTasks={progressData?.numberOfTasks}
        numberOfVideos={progressData?.numberOfVideos}
        readingProgress={
          status === 'waiting-for-reviews'
            ? progressData?.readingProgress
            : readingProgress
        }
        showAnswers={showProgressData}
        showReading={showProgressData || discrepancyInCompletion}
        showTaskPoints={showProgressData || discrepancyInCompletion}
        showVideos={showProgressData || discrepancyInCompletion}
        taskAnswers={progressData?.taskAnswers}
        taskMaxPoints={
          status === 'waiting-for-reviews'
            ? progressData?.taskMaxPoints
            : tasksPoints?.includes('/')
            ? parseInt(tasksPoints.split('/')[1], 10)
            : null
        }
        taskPoints={
          status === 'waiting-for-reviews'
            ? progressData?.taskPoints
            : tasksPoints?.includes('/')
            ? parseInt(tasksPoints.split('/')[0], 10)
            : null
        }
        taskPointsProgress={
          status === 'waiting-for-reviews'
            ? progressData?.taskPointsProgress
            : completion.tasksProgress
        }
        videoProgress={
          status === 'waiting-for-reviews'
            ? serverConfig.videoRequirementsType === 'individual'
              ? progressData?.videoProgressVideosWatched /
                progressData?.numberOfVideos
              : progressData?.videoProgress
            : videoProgress
        }
        videosWatched={progressData?.videoProgressVideosWatched}
      />

      <div
        className="completed"
        style={{ display: 'inline-flex', whiteSpace: 'nowrap', ...style }}
      >
        <div
          className="status"
          style={{ display: 'inline-block' }}
          {...(showTooltip || discrepancyInCompletion
            ? {
                [`data-completion-progress-status-${completion.id}`]: true
              }
            : {})}
        >
          <Badge
            category={badgeStatusColor}
            icon={
              status === 'failed'
                ? 'times-circle'
                : status === 'waiting-for-reviews'
                ? 'hourglass-start'
                : 'check'
            }
            isGroupedFirst={
              hasManuallySetIcon ||
              hasElectronicSignatureIcon ||
              hasCourseLanguageVersionIcon
            }
            isRounded={false}
            size="medium"
            text={
              <span>
                {text}

                {discrepancyInCompletion &&
                (status === 'failed' || status === 'success') ? (
                  <span
                    className="discrepancy"
                    style={{
                      display: 'inline-block',
                      fontSize: '0.6rem',
                      marginTop: 1,
                      padding: '0 2px',
                      verticalAlign: 'top'
                    }}
                  >
                    <Icon icon="asterisk" />
                  </span>
                ) : null}
              </span>
            }
          />
        </div>

        {hasCourseLanguageVersionIcon ? (
          <Badge
            category={badgeStatusColor}
            isGroupedFirst={hasManuallySetIcon || hasElectronicSignatureIcon}
            isGroupedLast={true}
            isRounded={false}
            size="medium"
            style={{
              marginLeft: 2,
              paddingLeft: 4,
              paddingRight:
                hasManuallySetIcon || hasElectronicSignatureIcon ? 3 : 5,
              position: 'relative'
            }}
            text={
              <div style={{ height: 17 }}>
                <FlagIcon
                  lang={completion?.courseLanguageVersion}
                  size="small"
                  style={{ padding: '1px 0', position: 'relative', top: -1 }}
                />
              </div>
            }
            tooltip={`${messages.courseLanguageVersion}: ${languageOption?.capitalizedName}`}
          />
        ) : null}

        {hasManuallySetIcon ? (
          <Badge
            category={badgeStatusColor}
            icon="cog"
            isGroupedFirst={hasElectronicSignatureIcon}
            isGroupedLast={true}
            isRounded={false}
            size="medium"
            style={{
              marginLeft: hasCourseLanguageVersionIcon ? 0 : 2,
              paddingLeft: 7,
              paddingRight: hasElectronicSignatureIcon ? 2 : 7
            }}
            tooltip={
              completion.manuallySetComments
                ? `${messages.completionManuallySet} - ${completion.manuallySetComments}`
                : messages.completionManuallySet
            }
          />
        ) : null}

        {hasElectronicSignatureIcon ? (
          <Badge
            category={badgeStatusColor}
            icon="signature"
            isGroupedLast={true}
            isRounded={false}
            size="medium"
            style={{
              marginLeft:
                hasCourseLanguageVersionIcon || hasManuallySetIcon ? 0 : 2,
              paddingLeft: 7,
              paddingRight: 7
            }}
            tooltip={messages.completionElectronicSignature}
          />
        ) : null}
      </div>
    </>
  );
};

export default CompletionStatus;
